import { useId, useState } from 'react';
import { Plus, X } from 'lucide-react';

/**
 * Common labels offered as datalist suggestions so keys stay consistent
 * across operators (a tidy CAFM auto-load). Free text is still allowed;
 * identifier fields with their own inputs (serial / registration) are
 * deliberately left off.
 */
const SUGGESTED_LABELS = [
  'Make',
  'Model',
  'Condition',
  'Colour',
  'Warranty expiry',
  'MAC address',
  'IMEI',
  'Asset owner',
  'Supplier',
  'PO number',
  'Purchase date',
];

interface Pair {
  label: string;
  value: string;
}

function toObject(rows: Pair[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = r.label.trim();
    const v = r.value.trim();
    if (k && v) out[k] = v; // last write wins on a duplicate label
  }
  return out;
}

/**
 * Optional free-form attribute capture for a tracked asset: repeatable
 * label / value pairs (e.g. Make -> Toshiba). Kept collapsed behind an
 * "Add additional info" button so it never slows the desk down; the
 * captured pairs enrich the asset record for the CAFM export.
 */
export function AdditionalInfoEditor({
  value,
  onChange,
}: {
  value: Record<string, string> | undefined;
  onChange: (attrs: Record<string, string>) => void;
}) {
  const listId = useId();
  const [rows, setRows] = useState<Pair[]>(() =>
    Object.entries(value ?? {}).map(([label, v]) => ({ label, value: v })),
  );
  const [open, setOpen] = useState(rows.length > 0);

  function commit(next: Pair[]) {
    setRows(next);
    onChange(toObject(next));
  }
  function addRow() {
    setOpen(true);
    setRows((r) => [...r, { label: '', value: '' }]);
  }
  function setRow(i: number, patch: Partial<Pair>) {
    commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function removeRow(i: number) {
    commit(rows.filter((_, idx) => idx !== i));
  }

  if (!open && rows.length === 0) {
    return (
      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
      >
        <Plus className="h-3.5 w-3.5" />
        Add additional info
      </button>
    );
  }

  return (
    <div className="w-full">
      <div className="text-[11px] font-medium uppercase tracking-wider text-fg-muted">
        Additional info
      </div>
      <datalist id={listId}>
        {SUGGESTED_LABELS.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
      <div className="mt-1.5 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              type="text"
              list={listId}
              value={r.label}
              onChange={(e) => setRow(i, { label: e.target.value })}
              placeholder="Field (e.g. Make)"
              className="w-32 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm text-fg focus:border-accent focus:outline-none"
            />
            <input
              type="text"
              value={r.value}
              onChange={(e) => setRow(i, { value: e.target.value })}
              placeholder="Value (e.g. Toshiba)"
              className="min-w-0 flex-1 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm text-fg focus:border-accent focus:outline-none"
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              aria-label="Remove"
              className="rounded-token-sm p-1 text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addRow}
        className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
      >
        <Plus className="h-3.5 w-3.5" />
        Add another
      </button>
    </div>
  );
}
